import { useRef, useState } from 'react';
import {
  DataTableWrapper,
  HasPermissionWrapperComponent,
  LanguagesV2,
} from '@wajeez-web/components/helpers';
import {
  Button,
  ButtonHeadless,
  UnderlinedTab,
  UnderlinedTabs,
} from '@wajeez-web/components/ui';
import {
  AnswersActions,
  QuestionsActions,
  contentEntityDataType,
  useContentDispatch,
  useContentSelector,
} from '@wajeez-web/stores/content';
import { CheckBoxType } from '@wajeez-web/components/mui';
import { HandleTableData } from '@wajeez-web/components/data-table';
import { useParams } from 'react-router-dom';
import { DownloadIcon, PlusIcon } from '@heroicons/react/outline';
import { CSVLink } from 'react-csv';
import { QuestionsModal } from '../components/QuestionsModal';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
import { AdminSystemPermissions } from '@wajeez-web/core/types';

const languageTabs: UnderlinedTab[] = [
  { value: LanguagesV2.AR, content: 'Arabic' },
  { value: LanguagesV2.EN, content: 'English' },
];

export const QuizQuestionsPage = () => {
  const { id } = useParams<{ id: string }>();

  const exportDataRef = useRef<any>(null);

  const contentDispatch = useContentDispatch();

  const [selectedTab, setSelectedTab] = useState<UnderlinedTab>({
    value: LanguagesV2.AR,
    content: 'Arabic',
  });
  const [isQuestionModalOpen, setIsQuestionModalOpen] = useState(false);
  const [isCreateAnswersLoading, setIsCreateAnswersLoading] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState<{
    questionId: number;
    text: string;
  }>();

  const datatable = useContentSelector(({ questions }) => questions.dataTable);

  const createQuestionStatus = useContentSelector(
    ({ questions }) => questions.create.status
  );
  const setCorrectAnswerStatus = useContentSelector(
    ({ answers }) => answers.setCorrect.status
  );
  const editQuestionStatus = useContentSelector(
    ({ questions }) => questions.update.status
  );

  const {
    tablePreferances,
    setTablePreferences,
    getData,
    isTableReferencesLoading,
  } = HandleTableData(
    'questions',
    QuestionsActions.getQuestionsAsDataTableAsync,
    undefined,
    { contentEntityId: id },
    [],
    undefined,
    selectedTab.value as number
  );

  const getExportedData = () => {
    const d = [];
    const dataTableData = datatable.data;

    if (dataTableData?.totalCount && dataTableData?.totalCount > 0) {
      for (let i = 0; i < dataTableData?.totalCount; i++) {
        const row: { [key: string]: any } = {};

        dataTableData?.['columns']?.forEach((item: any) => {
          if (!item.isHidden) {
            row[item.header] = item.cells[i];
          }
        });

        d.push(row);
      }
    }

    return d;
  };

  const onExport = async () => {
    if (datatable.data && tablePreferances) {
      exportDataRef.current.link.click();
    }
  };

  return (
    <div className="relative w-full">
      <CSVLink
        filename={'Quizzes.csv'}
        data={getExportedData()}
        ref={exportDataRef}
        className="hidden"
      >
        Download me
      </CSVLink>
      <div className="absolute -top-10 rtl:right-0 ltr:left-0 px-3 py-2 bg-[#fff4e5] text-[#663c00] text-[15px] rounded-md">
        <ReturnTranslatedText
          text={`general.${'Disclaimer The quiz will appear on the app as soon as 16 or more questions are published'}`}
          origintext="Disclaimer The quiz will appear on the app as soon as 16 or more questions are published"
        />
      </div>
      <div className="w-full flex justify-between items-center">
        <UnderlinedTabs
          tabsContent={languageTabs}
          selected={selectedTab}
          onSelect={(tab) => {
            setSelectedTab(tab);
          }}
          navStyle="w-full"
        />
        <HasPermissionWrapperComponent
          permissions={['132.0', '133.0', '133.2']}
          multiple
        >
          <div className="w-[120px]">
            <ButtonHeadless
              paddingYStyle="py-[6px]"
              action={() => {
                setIsQuestionModalOpen(true);
              }}
            >
              <div className="text-white text-[14px]">
                <ReturnTranslatedText
                  text={`buttons.${'Create Question'}`}
                  origintext="Create Question"
                />
              </div>
            </ButtonHeadless>
          </div>
        </HasPermissionWrapperComponent>
      </div>
      <div className="mt-6">
        <DataTableWrapper
          data={datatable.data as contentEntityDataType}
          onHeaderManage={(header) => {
            const flags = header
              ?.filter((h) => h.selected)
              .reduce(
                (previousValue: number, currentValue: CheckBoxType) =>
                  previousValue + Number(currentValue.value),
                0
              );

            tablePreferances &&
              flags &&
              setTablePreferences({
                ...tablePreferances,
                flags: flags + 1,
              });
          }}
          onPaginationChange={(pagination) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                pageNumber: pagination.pageNumber,
                pageSize: pagination.pageSize,
              });
          }}
          sort={tablePreferances?.sortingOperations?.[0]}
          onSort={(sort) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                sortingOperations: [sort],
              });
          }}
          onFilter={(filters, pagination) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                pageNumber: pagination.pageNumber,
                filteringOperations: filters.map((f) => {
                  return {
                    property: Number(f.key),
                    cretiria: f.value,
                    comparisonType: f.operator,
                  };
                }),
              });
          }}
          isLoading={datatable.status === 'loading'}
          rowActions={(row) => {
            return [
              {
                title: 'Edit',
                endpoint: '',
                permission: AdminSystemPermissions.questions.edit,
                type: 'request',
                action: () => {
                  setSelectedQuestion({
                    questionId: Number(row[1]),
                    text: row[4] as string,
                  });

                  setIsQuestionModalOpen(true);
                },
              },
              {
                title: row[16] === 'False' ? 'Publish' : 'Unpublish',
                endpoint: '',
                permission: AdminSystemPermissions.questions.changePublishState,
                type: 'request',
                action: async () => {
                  const res = await contentDispatch(
                    QuestionsActions.changeQuestionPublishStateAsync({
                      questionId: Number(row[1]),
                      publish: row[16] === 'False' ? true : false,
                    })
                  );

                  if (res.meta.requestStatus === 'fulfilled') {
                    getData();
                  }
                },
              },
              {
                title: 'Delete',
                endpoint: '',
                permission: AdminSystemPermissions.questions.delete,
                type: 'request',
                action: async () => {
                  const res = await contentDispatch(
                    QuestionsActions.deleteQuestionAsync(Number(row[1]))
                  );

                  if (res.meta.requestStatus === 'fulfilled') {
                    getData();
                  }
                },
              },
            ];
          }}
          extraActions={[
            {
              label: 'Export as CSV',
              onClick: () => {
                onExport();
              },
              Icon: <DownloadIcon className="h-4 w-4" />,
            },
          ]}
          preferences={tablePreferances || undefined}
          setPreferences={setTablePreferences}
        />
      </div>
      {isQuestionModalOpen && (
        <QuestionsModal
          data={selectedQuestion}
          isOpen={isQuestionModalOpen}
          onClose={() => {
            setSelectedQuestion(undefined);

            setIsQuestionModalOpen(false);
          }}
          onSubmit={async (data) => {
            if (data && id) {
              if (!selectedQuestion?.questionId) {
                const res = await contentDispatch(
                  QuestionsActions.createNewQuestionAsync({
                    contentEntityId: id,
                    languageId: selectedTab.value as number,
                    text: data.text || '',
                  })
                );

                if (res.meta.requestStatus === 'fulfilled') {
                  const questionId: number = res.payload as number;

                  if (data.answers) {
                    setIsCreateAnswersLoading(true);

                    const fetchedAnswers = await Promise.all(
                      data.answers.map((answer) => {
                        return contentDispatch(
                          AnswersActions.createNewAnswerAsync({
                            questionId,
                            text: answer.text,
                          })
                        );
                      })
                    );

                    setIsCreateAnswersLoading(false);

                    let isAllFulfilled = true;

                    for (const fetchedAnswer of fetchedAnswers) {
                      if (fetchedAnswer.meta.requestStatus !== 'fulfilled') {
                        isAllFulfilled = false;

                        break;
                      }
                    }

                    if (isAllFulfilled) {
                      const correctAnswer = data.answers.find(
                        (a) => a.isCorrect
                      );

                      const correctFetchedAnswer = fetchedAnswers.find(
                        (f) => f.meta.arg.text === correctAnswer?.text
                      );

                      const setCorrectAnswerRes = await contentDispatch(
                        AnswersActions.setCorrectAnswerAsync({
                          questionId,
                          answerId: correctFetchedAnswer?.payload as number,
                        })
                      );

                      if (
                        setCorrectAnswerRes.meta.requestStatus === 'fulfilled'
                      ) {
                        getData();

                        setSelectedQuestion(undefined);

                        setIsQuestionModalOpen(false);
                      }
                    }
                  }
                }
              } else {
                const res = await contentDispatch(
                  QuestionsActions.editQuestionAsync({
                    questionId: selectedQuestion.questionId,
                    text: data.text || '',
                  })
                );

                if (res.meta.requestStatus === 'fulfilled') {
                  if (data.answers) {
                    setIsCreateAnswersLoading(true);

                    const fetchedAnswers = await Promise.all(
                      data.answers.map((answer) => {
                        if (!answer.new) {
                          return contentDispatch(
                            AnswersActions.editAnswerAsync({
                              answerId: answer.id,
                              text: answer.text,
                            })
                          );
                        } else {
                          return contentDispatch(
                            AnswersActions.createNewAnswerAsync({
                              questionId: selectedQuestion.questionId,
                              text: answer.text,
                            })
                          );
                        }
                      })
                    );

                    setIsCreateAnswersLoading(false);

                    let isAllFulfilled = true;

                    for (const fetchedAnswer of fetchedAnswers) {
                      if (fetchedAnswer.meta.requestStatus !== 'fulfilled') {
                        isAllFulfilled = false;

                        break;
                      }
                    }

                    if (isAllFulfilled) {
                      const correctAnswer = data.answers.find(
                        (a) => a.isCorrect
                      );

                      const setCorrectAnswerRes = await contentDispatch(
                        AnswersActions.setCorrectAnswerAsync({
                          questionId: selectedQuestion.questionId,
                          answerId: correctAnswer?.id,
                        })
                      );

                      if (
                        setCorrectAnswerRes.meta.requestStatus === 'fulfilled'
                      ) {
                        getData();

                        setSelectedQuestion(undefined);

                        setIsQuestionModalOpen(false);
                      }
                    }
                  }
                }
              }
            }
          }}
          loading={
            createQuestionStatus === 'loading' ||
            editQuestionStatus === 'loading' ||
            isCreateAnswersLoading ||
            setCorrectAnswerStatus === 'loading'
          }
        />
      )}
    </div>
  );
};
